import { useEffect, useRef } from 'react';
import {
  useGetCandidateBatchQuery,
  useGetAnalysisRequirementsByCandidateBatchQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { SearchCandidateProfileMessageSender } from 'generated-graphql/types';
import { SearchCandidateVectorQueryMessage } from 'modules/candidate-profile/types/SearchCandidateVectorQueryMessage';
import { CandidateProfileListProps } from 'modules/candidate-profile/types/CandidateProfileListProps';
import { getCandidateBatchFromCandidateProfileListProps } from 'modules/candidate-profile/utils/getCandidateBatchFromCandidateProfileListProps';
import { shouldShowConversation } from 'modules/candidate-profile/utils/shouldShowConversation';

type UseAutoSendJobDetailsMessageProps = {
  candidateProfileListProps: CandidateProfileListProps;
  isVectorSearchInitialized: boolean;
  conversationHistoryLength: number;
  handlePlaceholderMessage: (
    message: SearchCandidateVectorQueryMessage
  ) => Promise<void>;
};

/**
 * Automatically sends an initial message with job details (title, location, requirements)
 * when navigating from analysis to source more candidates.
 */
export const useAutoSendJobDetailsMessage = ({
  candidateProfileListProps,
  isVectorSearchInitialized,
  conversationHistoryLength,
  handlePlaceholderMessage
}: UseAutoSendJobDetailsMessageProps) => {
  const candidateBatchId = getCandidateBatchFromCandidateProfileListProps(
    candidateProfileListProps
  );

  const shouldShowConversationValue = shouldShowConversation(
    candidateProfileListProps
  );

  // Track if initial auto-message has been sent
  const hasAutoSentInitialMessageRef = useRef(false);

  // Fetch candidate batch data to get job title and location
  const { data: candidateBatchData } = useGetCandidateBatchQuery({
    variables: { id: candidateBatchId || '' },
    skip: !candidateBatchId
  });

  // Fetch requirements separately
  const { data: requirementsData, loading: requirementsLoading } =
    useGetAnalysisRequirementsByCandidateBatchQuery({
      variables: { candidateBatchId: candidateBatchId || '' },
      skip: !candidateBatchId
    });

  // Auto-send initial message with job title when coming from analysis
  useEffect(() => {
    // Only run if conversation should be shown
    if (!shouldShowConversationValue) return;

    // Only run if we haven't sent the auto message yet
    if (hasAutoSentInitialMessageRef.current) return;

    // Only run if we have a candidateBatchId
    if (!candidateBatchId) return;

    // Only run if candidate batch data has loaded
    if (!candidateBatchData?.getCandidateBatch) return;

    // Only run if requirements data has finished loading
    if (requirementsLoading) return;

    // Only run if conversation is initialized and empty
    if (!isVectorSearchInitialized) return;
    if (conversationHistoryLength > 0) return;

    const candidateBatch = candidateBatchData.getCandidateBatch;

    // Get the job title
    const jobTitle = candidateBatch.jobDescriptionParsed?.jobTitle;

    // Only send if we have a job title
    if (!jobTitle) return;

    // Get the location
    const jobLocation = candidateBatch.jobDescriptionParsed?.location;

    // Get the requirements from the separate query
    const requirementItems =
      requirementsData?.getAnalysisRequirementsByCandidateBatch?.items || [];

    // Build the message
    let messageBody = `Job title: ${jobTitle}`;

    if (jobLocation) {
      messageBody += `\n\nLocation: ${jobLocation}`;
    }

    if (requirementItems.length > 0) {
      messageBody += '\n\nRequirements:';
      requirementItems.forEach((req) => {
        if (!req) return;
        // Use generatedRequirementText if available, otherwise use originalRequirementText
        const requirementText =
          req.generatedRequirementText || req.originalRequirementText;
        if (requirementText) {
          messageBody += `\n- ${requirementText}`;
        }
      });
    }

    // Mark as sent
    hasAutoSentInitialMessageRef.current = true;

    // Send the message
    handlePlaceholderMessage({
      id: 'auto-job-title-message',
      body: messageBody,
      date: new Date().toISOString(),
      sender: SearchCandidateProfileMessageSender.User
    });
  }, [
    candidateBatchId,
    candidateBatchData,
    requirementsData,
    requirementsLoading,
    isVectorSearchInitialized,
    conversationHistoryLength,
    handlePlaceholderMessage
  ]);
};
